import { useCallback, useEffect, useMemo, useState } from 'react'
import { createAllTimeRange, createTimeRange, fetchEarliestDate } from '../utils/dateUtils'

/**
 * Manages time range selection, supporting an "All time" option by fetching
 * the earliest available date from the API.
 * @param {object} initialTimeRange The initial time range configuration.
 * @param {string} initialTimeRange.duration Duration identifier (e.g., 'allTime', 'last7days').
 * @param {string} [initialTimeRange.startTimeISO] Optional ISO start time.
 * @param {string} [initialTimeRange.endTimeISO] Optional ISO end time.
 * @param {function} onTimeRangeChange IMPORTANT: Should be memoized with useCallback in the
 * parent component. This callback is invoked when the time range changes.
 * @returns {{earliestDate: string|null, isLoading: boolean, error: string|null, handleTimeRangeChange: function}}
 * An object containing the earliest date, loading state, error state, and the time range change handler.
 */
export function useTimeRangeManager(initialTimeRange, onTimeRangeChange) {
  const [earliestDate, setEarliestDate] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false

    if (initialTimeRange?.duration === 'allTime' && !initialTimeRange.startTimeISO) {
      const fetchAndSetEarliest = async () => {
        setIsLoading(true)
        setError(null)
        try {
          const earliest = await fetchEarliestDate()
          if (cancelled) return

          if (!earliest) {
            setError('Failed to fetch earliest date')
            return
          }
          setEarliestDate(earliest)
          const timeRangeObj = await createAllTimeRange(earliest)
          // Update time range with fetched earliest date for initial 'allTime' selection
          onTimeRangeChange?.(timeRangeObj)
        } catch (err) {
          if (cancelled) return
          console.error('Error fetching earliest date in useTimeRangeManager:', err)
          setError(err?.message || 'Failed to fetch earliest date')
        } finally {
          if (!cancelled) {
            setIsLoading(false)
          }
        }
      }

      fetchAndSetEarliest()
    }

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTimeRange?.duration, initialTimeRange?.startTimeISO, initialTimeRange?.endTimeISO])
  // onTimeRangeChange intentionally omitted to prevent infinite loops

  const handleTimeRangeChange = useCallback(async (newRange) => {
    if (newRange === 'allTime') {
      if (!earliestDate) {
        // Earliest date is required for "All time" but not yet available.
        setError('Earliest date not available yet for "All time" selection.')
        return null
      }
      const timeRangeObj = await createAllTimeRange(earliestDate)
      onTimeRangeChange?.(timeRangeObj)
      return timeRangeObj
    }

    const timeRangeObj = typeof newRange === 'number' ? await createTimeRange(newRange) : newRange
    onTimeRangeChange?.(timeRangeObj)
    return timeRangeObj
  }, [earliestDate, onTimeRangeChange])

  return useMemo(() => ({
    earliestDate,
    isLoading,
    error,
    handleTimeRangeChange,
  }), [earliestDate, isLoading, error, handleTimeRangeChange])
}